import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { ApiError } from '@/api/http';
import { queryKeyRoots } from '@/api/queryKeys';
import { createRole, updateRole, type Role } from '@/api/vacancies';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Dialog } from '@/components/ui/Dialog';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';

import {
  changesOf,
  creationOf,
  fieldsOf,
  vacancyFormSchema,
  type VacancyForm,
} from './vacancyFields';

interface Props {
  /**
   * The vacancy being edited, or **nothing at all** for a new one — which is what makes this one
   * dialog rather than two. The board's criterion is *"Edit (the same dialog, pre-filled)"*: a
   * create form and an edit form that describe the same vacancy in two different ways is the drift
   * `personFields.ts` was extracted to prevent, and here the whole form is small enough that one
   * component **is** the extraction (a `VacancyFields` component would have exactly one caller,
   * which is Sprint 8's `applyTenantAccent` in miniature).
   */
  vacancy?: Role;
  onClose: () => void;
}

/**
 * §6.8's **New vacancy** and its **Edit** — Admin only, and the only two writes on this screen that
 * take a form (closing is `CloseVacancyDialog`, which takes a decision).
 *
 * ⚠️ **There is no *Delete* here and there cannot be one** (ADR 0005, **feedback protection #2**).
 * A vacancy is *closed* — `PATCH {"is_open": false}` — and `RoleUpdate`'s own docstring says that is
 * what "delete" means for a vacancy, *"which is not deletable through any surface"*. The API offers
 * no `DELETE` operation on any role path to call, `http.ts` exposes no helper that could issue one,
 * and `vacancyWrites.test.tsx` asserts all three halves of that: the document, the client's verbs,
 * and that no control on this screen — **with either dialog open** — matches `/delete|remove/i`.
 *
 * ⚠️ **The open flag is offered when creating and not when editing.** §6.8 puts it in the create
 * dialog and it is an ordinary field there: a vacancy being entered for a position already filled is
 * created closed in one step. Editing is different, because closing is the one state change that has
 * something to say for itself — what stays, and that it is reversible — and a checkbox inside a form
 * whose button reads *Save changes* says none of it. So the menu's **Close** / **Reopen** owns that
 * write, with the sentences it owes, and this form cannot make it silently.
 *
 * ⚠️ **`PATCH` sends only what changed** (`changesOf`), and nothing at all when nothing did: an empty
 * `PATCH` answers 200 and touches `updated_at` for no reason. `POST` sends exactly `RoleIn`'s four
 * fields (`creationOf`) and **never a tenant** — that comes from the session
 * (`.claude/rules/tenancy.md`).
 *
 * ⚠️ **The answer is thrown away and `queryKeyRoots.roles` is invalidated instead.** That root is the
 * family's first segment, so it reaches every catalogue page and every narrower entry filed under it
 * (`queryKeys.roles`) — it named `queryKeys.role(roleId)` as that narrower entry until 2026-09-28,
 * when BUG-079 deleted a builder no query read. `RoleOut` carries `active_question_count` and
 * `active_candidate_count`: a write that refreshed one page and left another stale would show two
 * truths about one vacancy in one session.
 * Nothing is written into the cache by hand — a `setQueryData` here would put the row on screen
 * without the server having agreed to it, and the grid's own request is what confirms it landed.
 */
export function VacancyDialog({ vacancy, onClose }: Props) {
  const { t } = useTranslation(['vacancies', 'common', 'components']);
  const queryClient = useQueryClient();

  const editing = vacancy !== undefined;

  const form = useForm<VacancyForm>({
    resolver: zodResolver(vacancyFormSchema),
    // Mounted when it opens and unmounted when it closes (`VacancyMenu`, the screen), so the vacancy
    // as it is now *is* the default: no effect resets the form, and a half-typed title cannot survive
    // a refetch that changed the row underneath it.
    defaultValues: vacancy
      ? fieldsOf(vacancy)
      : { title: '', department: '', abbreviation: '', is_open: true },
  });
  const { errors } = form.formState;
  const isOpenFlag = form.watch('is_open');
  const openFlagId = useId();

  const save = useMutation<Role, ApiError, VacancyForm>({
    mutationFn: (submitted) =>
      vacancy
        ? updateRole(vacancy.id, changesOf(vacancy, submitted))
        : createRole(creationOf(submitted)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeyRoots.roles });
      onClose();
    },
  });

  const submit = form.handleSubmit(async (submitted) => {
    // Nothing was touched, so there is nothing to write.
    if (vacancy && Object.keys(changesOf(vacancy, submitted)).length === 0) return onClose();

    try {
      await save.mutateAsync(submitted);
    } catch {
      // Held by the mutation and rendered below; rethrowing reaches only the console.
    }
  });

  /**
   * §8 inside a dialog: what failed, in words, never a status code and never the server's English
   * `detail` (§8, §11).
   *
   * ⚠️ **Offline gets this screen's own sentence rather than either shared one.**
   * `components:state.offline` **promised** *"changes will be sent when the connection returns"* until
   * BUG-017 split it into the failed-**read** sentence and `state.offlineWrite` for a failed write;
   * nothing queues this request, then or now.
   *
   * **Why this screen still keeps its own** rather than adopting `state.offlineWrite`: status 0 means no
   * answer arrived, so the client does not know whether the vacancy was created — and on a `POST` a
   * confident retry is how a **second vacancy for one position** gets made. So the sentence names the
   * **catalogue** as the thing to reload before trying again, which is the check that prevents that
   * duplicate; a sentence shared with four other writes can say *"reload the page"* and not which page.
   * `QuestionDialog` keeps its own for the same duplication risk on the bank, and `AnonymizeDialog`
   * because its write is irreversible (ADR 0009).
   */
  const failure = (() => {
    if (!save.error) return null;
    if (save.error.isOffline) return t('vacancies:dialog.offline');
    if (save.error.isThrottled) return t('components:state.throttled');
    return t(editing ? 'vacancies:edit.failed' : 'vacancies:new.failed');
  })();

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        // Esc and the overlay cancel, and cancelling writes nothing. Radix owns the focus trap and
        // the key handling, and the amended `Dialog` (TASK-069) puts focus on the content and gives
        // it back to the opener — nothing here defeats either.
        if (!next) onClose();
      }}
      title={
        vacancy
          ? t('vacancies:edit.title', { title: vacancy.title })
          : t('vacancies:new.dialogTitle')
      }
      description={t(editing ? 'vacancies:edit.description' : 'vacancies:new.description')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common:cancel')}
          </Button>
          <Button
            // Not `type="submit"`: this renders in the dialog's actions row, outside the `<form>`
            // below. Enter inside the form still submits, through the same handler.
            onClick={() => void submit()}
            isLoading={save.isPending}
          >
            {t(editing ? 'vacancies:edit.submit' : 'vacancies:new.submit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <form className="flex flex-col gap-4" onSubmit={(event) => void submit(event)} noValidate>
          {/* Errors render **below their field** through `Field`, never as a toast (§4 Forms). */}
          <Field
            label={t('vacancies:form.title')}
            required
            error={errors.title ? t('vacancies:form.errors.title') : undefined}
          >
            {(fieldProps) => (
              <Input {...fieldProps} {...form.register('title')} autoComplete="off" />
            )}
          </Field>

          <Field
            label={t('vacancies:form.department')}
            required
            error={errors.department ? t('vacancies:form.errors.department') : undefined}
          >
            {(fieldProps) => (
              <Input {...fieldProps} {...form.register('department')} autoComplete="off" />
            )}
          </Field>

          <Field
            label={t('vacancies:form.abbreviation')}
            required
            error={errors.abbreviation ? t('vacancies:form.errors.abbreviation') : undefined}
          >
            {/*
             * ⚠️ **No `maxLength` attribute**, deliberately — `vacancyFields.ts` records why: a
             * bound the browser enforces silently is a bound nobody is told about, and the fifth
             * character has to be typeable for its refusal to be visible at all.
             */}
            {(fieldProps) => (
              <Input {...fieldProps} {...form.register('abbreviation')} autoComplete="off" />
            )}
          </Field>

          {/*
           * The open flag, when creating. Editing does not offer it — see this module's header: the
           * one way to close a vacancy is the action that explains what closing does.
           */}
          {!editing && (
            <Checkbox
              id={openFlagId}
              checked={isOpenFlag}
              onCheckedChange={(next) =>
                form.setValue('is_open', next === true, { shouldValidate: true })
              }
              label={t('vacancies:form.open')}
            />
          )}
        </form>

        {failure && (
          <p role="alert" className="text-sm text-negative">
            {failure}
          </p>
        )}
      </div>
    </Dialog>
  );
}
